import type { FC } from 'react';
import React, { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop';
import { FormattedMessage } from 'react-intl-next';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';

import Button from '@atlaskit/button/custom-theme-button';
import AddCommentIcon from '@atlaskit/icon/core/comment';
import Tooltip from '@atlaskit/tooltip';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	useIsObjectSidebarShown,
	useObjectSidebarActions,
} from '@confluence/object-sidebar-api/entry-points/useObjectSidebar';
import { VIEW_PAGE_CONTEXT_MENU_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName';
import {
	ExperienceStart,
	ExperienceSuccess,
} from '@confluence/experience-tracker/entry-points/component';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary';
import { expValEquals } from '@confluence/feature-experiments';
import { BannerStateContainer } from '@confluence/banners/entry-points/BannerStateContainer';
import {
	useCommentsContentState,
	useCommentsContentActions,
} from '@confluence/comment-context/entry-points/useCommentsContent';
import { CommentWarningDialog } from '@confluence/comment-dialogs';
import { useAddCommentPermissionCheck } from '@confluence/comments-hooks/entry-points/useAddCommentPermissionCheck';
import { useSearchSessionId } from '@confluence/search-session/entry-points/SearchSessionState';
import { DialogsStateContainer } from '@confluence/dialogs/entry-points/DialogsStateContainer';
import { usePageState } from '@confluence/page-context';
import { useDocumentUpdateStatus } from '@confluence/annotation-provider-store/entry-points/DocumentUpdateStatusContext';

import { Popup, type Position } from './Popup';
import { getCreateButtonDisabledMessage } from './getCreateButtonDisabledMessage';
import { getRange, findStickyHeader } from './highlightActionsUtils';
import { getPosition } from './highlightActionsSimpleUtils';
import {
	BuiltInActionsSimple,
	DisabledButtonWrapper,
	DisabledButtonOverlay,
} from './highlightActionsStyled';

export type SelectionRect = {
	top: number;
	left: number;
	width: number;
	height: number;
};

export type ActiveSelection = {
	selectedText: string;
	selectedRange: Range;
};

export type HighlightActionsSimpleProps = {
	contentId: string;
	isArchived?: boolean;
	contentStatusError?: ApolloErrorInterop;
	isAnnotationAllowed?: boolean;
	range?: Range;
	onCommentButtonPress?: () => void;
	needTransition?: boolean;
	onMouseEnter?: () => void;
	onMouseLeave?: () => void;
	onCommentButtonMount?: () => void;
};

export const HighlightActionsSimple: FC<HighlightActionsSimpleProps> = ({
	contentId,
	isArchived,
	contentStatusError,
	range,
	isAnnotationAllowed,
	onCommentButtonPress,
	needTransition,
	onMouseEnter,
	onMouseLeave,
	onCommentButtonMount,
}) => {
	const { hasContentChanged } = useCommentsContentState();
	const { resetContentChanged } = useCommentsContentActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [position, setPosition] = useState<Position | null>(null);
	const [{ searchSessionId, additionalAnalytics }] = useSearchSessionId();
	const isObjectSidebarShown = useIsObjectSidebarShown();
	const { hideObjectSidebar } = useObjectSidebarActions();
	const [{ contentType }] = usePageState();
	const { canAddComments, contentQueryError } = useAddCommentPermissionCheck(contentId);
	const { documentNeedsUpdate, currentDocumentVersion, publishedDocumentVersion } =
		useDocumentUpdateStatus();

	const mountedRef = useRef(false);
	const screenEventFiredRef = useRef(false);

	useLayoutEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
		};
	}, []);

	useEffect(() => {
		if (range) {
			const pos = getPosition(range);
			setPosition(pos);
		} else {
			setPosition(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [range]);

	const createReactInlineCommentHighlight = () => {
		if (isObjectSidebarShown) {
			void hideObjectSidebar();
		}
		onCommentButtonPress && onCommentButtonPress();
	};

	const handleCommentButtonClick = (dialogs) => {
		if (hasContentChanged) {
			dialogs.showDialog(CommentWarningDialog, {
				onConfirm: () => {
					resetContentChanged();
					createReactInlineCommentHighlight();
				},
			});
		} else {
			createReactInlineCommentHighlight();
		}

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'highlightActionsMenu',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'createInlineCommentFromHighlightActionsMenu',
				type: 'ui',
				attributes: {
					contentId,
					pageMode: 'view',
					searchSessionId,
					...additionalAnalytics,
				},
			},
		}).fire();
	};

	const createInlineCommentButton = () => {
		onCommentButtonMount && onCommentButtonMount();

		const needsUpdateToComment =
			(!!documentNeedsUpdate &&
				expValEquals('confluence_fe_disable_comment_if_page_outdated', 'isEnabled', true)) ||
			(currentDocumentVersion !== publishedDocumentVersion &&
				expValEquals('confluence_fe_disable_comment_if_page_outdated_v2', 'isEnabled', true));
		const isCreateButtonEnabled =
			!isArchived && canAddComments && !needsUpdateToComment && isAnnotationAllowed;

		const createButton = (
			<Subscribe to={[DialogsStateContainer]}>
				{(dialogs: DialogsStateContainer) => (
					<Button
						appearance="subtle"
						data-key="com.atlassian.confluence.plugins.confluence-inline-comments:create-inline-comment" // Needed for integration tests
						iconBefore={<AddCommentIcon label="" spacing="spacious" />}
						isDisabled={!isCreateButtonEnabled}
						testId="createInlineCommentButton"
						onClick={() => {
							handleCommentButtonClick(dialogs);
						}}
					>
						<FormattedMessage
							id="highlight-actions.inline-comments.comment"
							defaultMessage="Comment"
							description="Make a new comment"
						/>
					</Button>
				)}
			</Subscribe>
		);

		// WS-2802 - We need to wrap the disabled button and put a transparent absolute overlay over the button
		//           in order to show the tooltip correctly as the disabled button swallows all events on the element
		return isCreateButtonEnabled ? (
			<Fragment key="inline-comment-button">{createButton}</Fragment>
		) : (
			<Tooltip
				delay={0}
				position="top"
				content={getCreateButtonDisabledMessage(
					isArchived,
					canAddComments,
					needsUpdateToComment,
					contentType,
				)}
			>
				<DisabledButtonWrapper key="inline-comment-button">
					<DisabledButtonOverlay />
					{createButton}
				</DisabledButtonWrapper>
			</Tooltip>
		);
	};

	const renderPopup = (component) => (
		<Subscribe to={[BannerStateContainer]}>
			{(bannerState: BannerStateContainer) => {
				// if there's a sticky header we want to position the toolbar below
				// the nav, the page header (if visible) and the sticky header
				let stickyHeaderHeight = 0;
				const range = getRange();
				if (range) {
					const header = findStickyHeader(range.commonAncestorContainer);
					if (header && bannerState) {
						stickyHeaderHeight = bannerState.getTotalHeight() + header.clientHeight;
					}
				}

				if (!screenEventFiredRef.current) {
					createAnalyticsEvent({
						type: 'sendScreenEvent',
						data: {
							name: 'highlightActionsMenu',
							attributes: {
								contentId,
							},
						},
					}).fire();
				}
				screenEventFiredRef.current = true;

				return (
					<Fragment>
						{position && (
							<Popup
								top={stickyHeaderHeight + position.top}
								left={position.left}
								needTransition={needTransition}
								onMouseEnter={onMouseEnter}
								onMouseLeave={onMouseLeave}
							>
								{component}
							</Popup>
						)}

						<ExperienceSuccess name={VIEW_PAGE_CONTEXT_MENU_EXPERIENCE} />
					</Fragment>
				);
			}}
		</Subscribe>
	);

	const error = contentStatusError || contentQueryError;
	return (
		<Fragment>
			{error && <ErrorDisplay error={error} />}
			<ErrorBoundary attribution={Attribution.BACKBONE}>
				<ExperienceStart id={contentId} name={VIEW_PAGE_CONTEXT_MENU_EXPERIENCE} />
				{renderPopup(
					<BuiltInActionsSimple key="built-in-actions">
						{createInlineCommentButton()}
					</BuiltInActionsSimple>,
				)}
			</ErrorBoundary>
		</Fragment>
	);
};
